import { PortFormRequestDto } from '@state';
import { portAnalysisApi } from './portAnalysisServiceApi';
import { PortAnalysisPortResponse } from '@state/types/PortAnalysisPortResponse';
import { PortPceAvailabilityResponse } from '@state/types/PortPceAvailabilityResponse';
import { PortAnalysisPortSearchResponse } from '@state/types/PortAnalysisPortSearchResponse';
import {
  SuggestedExpenseSegment,
  SuggestedExpensesForm
} from '@state/types/SuggestedExpenses';

const BASE_URL = '/secured/port';

const portAPI = portAnalysisApi.injectEndpoints({
  endpoints: (build) => ({
    fetchPort: build.query<PortAnalysisPortResponse, { portId: number }>({
      query: ({ portId }) => ({
        url: `${BASE_URL}/${portId}`,
        method: 'GET'
      }),
      providesTags: (res, _error, arg) =>
        res
          ? [{ type: 'PORT_ANALYSIS_PORT' as const, id: arg.portId }]
          : ['PORT_ANALYSIS_PORT']
    }),
    searchPort: build.query<
      PortAnalysisPortSearchResponse[],
      { portName: string }
    >({
      query: ({ portName }) => ({
        url: `${BASE_URL}/search`,
        params: { portName },
        method: 'GET'
      }),
      providesTags: ['PORT_ANALYSIS_PORT_SEARCH']
    }),
    savePort: build.mutation<PortAnalysisPortResponse, PortFormRequestDto>({
      query: (portData) => ({
        url: BASE_URL,
        method: 'POST',
        data: portData
      }),
      invalidatesTags: (res, _error, arg) => [
        res
          ? { type: 'PORT_ANALYSIS_PORT' as const, id: res.id }
          : 'PORT_ANALYSIS_PORT',
        'PORT_ANALYSIS_LOCATION',
        'PORT_ANALYSIS_PORT_SEARCH'
      ]
    }),
    fetchSuggestedExpenses: build.query<
      SuggestedExpenseSegment[],
      { portId: number }
    >({
      query: ({ portId }) => ({
        url: `${BASE_URL}/${portId}/suggested-expenses`,
        method: 'GET'
      }),
      // segments with no applicable expenses may come back without the list
      transformResponse: (segments: SuggestedExpenseSegment[] | null) =>
        (segments ?? []).map((s) => ({ ...s, expenses: s.expenses ?? [] })),
      providesTags: (_res, _error, arg) => [
        { type: 'PORT_ANALYSIS_SUGGESTED_EXPENSES' as const, id: arg.portId }
      ]
    }),
    saveSuggestedExpenses: build.mutation<
      SuggestedExpenseSegment[],
      { portId: number; form: SuggestedExpensesForm }
    >({
      query: ({ portId, form }) => ({
        url: `${BASE_URL}/${portId}/suggested-expenses`,
        method: 'PUT',
        data: form
      }),
      invalidatesTags: (_res, _error, arg) => [
        { type: 'PORT_ANALYSIS_SUGGESTED_EXPENSES' as const, id: arg.portId }
      ]
    }),
    pceAvailability: build.mutation<
      PortPceAvailabilityResponse,
      { portId: number; state: boolean }
    >({
      query: ({ portId, state }) => ({
        url: `${BASE_URL}/${portId}/available-in-pce`,
        method: 'PATCH',
        params: { state }
      }),
      invalidatesTags: (_res, _error, arg) => [
        { type: 'PORT_ANALYSIS_PORT' as const, id: arg.portId }
      ]
    })
  }),
  overrideExisting: false
});

export const {
  useFetchPortQuery,
  useLazySearchPortQuery,
  useLazyFetchPortQuery,
  useSavePortMutation,
  usePceAvailabilityMutation,
  useFetchSuggestedExpensesQuery,
  useSaveSuggestedExpensesMutation
} = portAPI;
